import ParentCss from "../../style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getActiveOrganization } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Async from "utils/Async";
import BusinessesActions from "actions/BusinessesActions";
import Countries from "const/Countries";
import IntegrationServices from "const/IntegrationServices";
import IntegrationsActions from "actions/IntegrationsActions";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Step from "../Step";
import TasksActions from "actions/TasksActions";
import UiActions from "actions/UiActions";
import UiRoutes from "const/UiRoutes";
import Urls from "utils/Urls";
import classNames from "classnames";

const SyncEntitiesStep = (props) => {
  const history = useHistory();

  const {
    index: stepIndex,
    currentStep,
    serviceToConnect,
    rpaMode,
    setNewBusinessId
  } = props;

  const dispatch = useDispatch();

  const integrationServiceData = IntegrationServices.getByValue(serviceToConnect);

  const { code: envVarsCode, error: envVarsError } = useMemo(() => {
    return Urls.parseQueryString(window.location.search);
  }, []);

  const { messages, uiTexts } = useSelector(getTextsData);

  const { countryCode } = useSelector(getActiveOrganization);

  const usCountry = countryCode === Countries.US;

  const xeroService = serviceToConnect === IntegrationServices.XERO.value;

  const quickBooksService = serviceToConnect === IntegrationServices.QUICK_BOOKS.value;

  const zohoService = serviceToConnect === IntegrationServices.ZOHO.value;

  const [dataSyncTextA] = messages.onboardingSyncEntities;

  const [integrationSyncStep, setIntegrationSyncStep] = useState(0);

  const integrationStarted = useRef(false);

  const integrationSteps = useMemo(() => {
    if (!serviceToConnect) return [];

    return [
      [
        IntegrationsActions.integrateBusinesses,
        quickBooksService ? uiTexts.syncingBusiness : uiTexts.syncingBusinesses,
        Icons.Briefcase
      ],
      [IntegrationsActions.syncAccounts, uiTexts.syncingAccounts, Icons.CreditCard],
      ...(rpaMode
        ? []
        : [
          [IntegrationsActions.syncClasses, uiTexts.syncingClasses, Icons.Stack],
          (xeroService || quickBooksService) && [IntegrationsActions.syncItems, uiTexts.syncingItems, Icons.Cube],
          (xeroService || zohoService || (quickBooksService && !usCountry))
        && [IntegrationsActions.syncTaxRates, uiTexts.syncingTaxRates, Icons.Percent],
          quickBooksService && [IntegrationsActions.syncLocations, uiTexts.syncingLocations, Icons.MapPin],
          [IntegrationsActions.syncContacts, quickBooksService
            ? uiTexts.syncingContactsAndProjects : uiTexts.syncingContacts, Icons.Users],
          zohoService && [IntegrationsActions.syncProjects, uiTexts.syncingProjects, Icons.Kanban],
          (xeroService || quickBooksService) && [IntegrationsActions.syncDocuments, uiTexts.syncingDocuments, Icons.FileText],
          [IntegrationsActions.syncTransactions, uiTexts.syncingTransactions, Icons.Repeat]
        ])
    ].filter(Boolean);
  }, [serviceToConnect, rpaMode, usCountry, xeroService, quickBooksService, zohoService, uiTexts]);

  const runIntegrationSteps = useCallback(async() => {
    let newBusinessIds = [];

    await Async.runInSequence(integrationSteps.map(([action], index) => {
      return async() => {
        if (index) {
          if (newBusinessIds.length) {
            setIntegrationSyncStep(index);
            await dispatch(action(serviceToConnect, newBusinessIds, true, true, true));
          }
        } else {
          let selectedItems = null;

          let extraData = { rpaMode: !!rpaMode };

          if (integrationServiceData.manualSelection) {
            const { businesses: serviceBusinessesList, ...restResponseData } = await dispatch(action(serviceToConnect, true));

            extraData = restResponseData;
            if (Array.isArray(serviceBusinessesList) && serviceBusinessesList.length) {
              if (serviceBusinessesList.length > 1) {
                const modalResult = await dispatch(UiActions.showSelectIntegrationBusinessesWindow({
                  businessesList: serviceBusinessesList,
                  service: serviceToConnect
                }));

                selectedItems = modalResult || [];
              } else selectedItems = [serviceBusinessesList[0].itemId];
            }
          }

          const { businesses: businessesData } = await dispatch(action(serviceToConnect, false, selectedItems, extraData));

          if (Array.isArray(businessesData) && businessesData.length) newBusinessIds = businessesData.map(({ id }) => id);
          window.history.replaceState({}, document.title, UiRoutes.MAIN);
        }
      };
    }));

    const allBusinessesData = await dispatch(BusinessesActions.fetchBusinessesList(false, true, true));

    dispatch(TasksActions.fetchTasksList(true));

    const [businessId] = newBusinessIds;

    if (!businessId) {
      history.push(allBusinessesData.length === 1 ? `/${allBusinessesData[0].id}` : UiRoutes.MAIN);

      return;
    }
    await dispatch(BusinessesActions.fetchGlobalStats());
    if (!rpaMode && quickBooksService) {
      setNewBusinessId(businessId);
    } else {
      history.push(newBusinessIds.length === 1 ? `/${businessId}` : UiRoutes.MAIN);
    }
  }, [
    serviceToConnect,
    integrationServiceData,
    integrationSteps,
    quickBooksService,
    rpaMode,
    history,
    dispatch,
    setNewBusinessId
  ]);

  const [, statusText, StatusIcon] = (
    integrationSteps[integrationSyncStep]
    || [null, uiTexts.settingUp, Icons.Spinner]
    || []);

  useEffect(() => {
    if (!integrationSteps.length) return;
    if (envVarsCode) {
      if (!envVarsError && !integrationStarted.current) {
        integrationStarted.current = true;
        runIntegrationSteps();
      }
    } else {
      setIntegrationSyncStep(-1);
    }
  }, [envVarsCode, envVarsError, integrationSteps.length, runIntegrationSteps]);

  return (
    <Step
      number={stepIndex + 1}
      active={currentStep === stepIndex}
      success={currentStep > stepIndex}>
      <div className={ParentCss.row}>
        <div className={ParentCss.col}>
          <div className={ParentCss.subtitle}>
            {dataSyncTextA}
          </div>
          {(currentStep === stepIndex) && (
            <>
              <div className={classNames(ParentCss.progress, ParentCss.animated)}>
                <div />
              </div>
              {!!statusText && (
                <div className={ParentCss.description}>
                  <StatusIcon data-wait={integrationSteps[integrationSyncStep] ? undefined : ""} />
                  <span>{statusText}</span>
                </div>
              )}
            </>
          )}
        </div>
        {(currentStep > stepIndex) && (
          <div className={`${ParentCss.likeButton} ${ParentCss.success}`}>
            <Icons.Check weight="bold" />
            <span>{uiTexts.synced}</span>
          </div>
        )}
      </div>
    </Step>
  );
};

export default React.memo(SyncEntitiesStep);
